import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { OrderStatus } from "../types/order/IOrder";

type SetSearchParams = ReturnType<typeof useSearchParams>[1];

// URL is the single source of truth for the filters. These helpers derive the
// filter state from the current search params so both the initial render and
// later navigations (shared links, browser back) stay in sync with the URL.
const parseStatusParam = (params: URLSearchParams): OrderStatus[] => {
  const statusParam = params.get("status");
  if (!statusParam) return [];
  return statusParam
    .split(",")
    .filter((status) =>
      Object.values(OrderStatus).includes(status as OrderStatus),
    ) as OrderStatus[];
};

const parseDriverParam = (params: URLSearchParams): string | undefined =>
  params.get("driver") ?? undefined;

const parseCustomerParam = (params: URLSearchParams): string =>
  params.get("customer") ?? "";

/**
 * Encapsulates the All Orders page filter state and its URL-sync / debounce /
 * "isFiltering" lifecycle. The URL is authoritative: filters are hydrated from
 * the search params on mount and re-synced whenever the params change, and we
 * never write empty params on mount (which previously stripped shared-link
 * filters before the first search ran).
 */
export const useAllOrdersFilters = (
  searchParams: URLSearchParams,
  setSearchParams: SetSearchParams,
) => {
  const [statusFilter, setStatusFilter] = useState<OrderStatus[]>(() =>
    parseStatusParam(searchParams),
  );
  const [driverFilter, setDriverFilter] = useState<string | undefined>(() =>
    parseDriverParam(searchParams),
  );
  const [customerSearchText, setCustomerSearchText] = useState<string>(() =>
    parseCustomerParam(searchParams),
  );

  // Flag to track if filtering is in progress
  const [isFiltering, setIsFiltering] = useState<boolean>(
    () =>
      parseStatusParam(searchParams).length > 0 ||
      Boolean(parseDriverParam(searchParams)) ||
      parseCustomerParam(searchParams) !== "",
  );

  // Marks the render that follows an external URL change so the URL-write
  // effect below can skip that pass. Without this the write effect runs in the
  // same commit as the sync effect while statusFilter/driverFilter/
  // debouncedCustomerSearchText still hold their previous (stale) values, and
  // would write the old filters straight back to the URL — defeating browser
  // back / shared links that intentionally drop filters.
  const syncingFromUrlRef = useRef(false);

  // Separate debounced state so we don't rewrite the URL on every keystroke.
  const [debouncedCustomerSearchText, setDebouncedCustomerSearchText] =
    useState<string>(() => parseCustomerParam(searchParams));

  // Keep local state in sync with the URL. When params are removed (browser
  // back or a shared link without filters) the state is reset instead of
  // keeping stale filters that would be written straight back to the URL. The
  // debounced value is reset in the same pass so it can't lag behind and write
  // the previous customer search back to the URL after an external change.
  useEffect(() => {
    syncingFromUrlRef.current = true;
    const nextCustomer = parseCustomerParam(searchParams);
    setStatusFilter(parseStatusParam(searchParams));
    setDriverFilter(parseDriverParam(searchParams));
    setCustomerSearchText(nextCustomer);
    setDebouncedCustomerSearchText(nextCustomer);
  }, [searchParams]);

  // Debounce the customer search text
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedCustomerSearchText(customerSearchText);
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [customerSearchText]);

  // Update URL based on the debounced search text
  useEffect(() => {
    // Skip the pass triggered by an external URL change: the filter state was
    // just re-synced from the URL above and any read here would be stale.
    if (syncingFromUrlRef.current) {
      syncingFromUrlRef.current = false;
      return;
    }

    const newSearchParams = new URLSearchParams();

    // Preserve the order ID if it exists (gjør dette først for å sikre at ID-parameteren alltid er med)
    const orderId = searchParams.get("id");
    if (orderId) {
      newSearchParams.set("id", orderId);
    }

    // Update status parameter
    if (statusFilter.length > 0) {
      newSearchParams.set("status", statusFilter.join(","));
    }

    // Update driver parameter
    if (driverFilter) {
      newSearchParams.set("driver", driverFilter);
    }

    // Update customer parameter
    if (debouncedCustomerSearchText) {
      newSearchParams.set("customer", debouncedCustomerSearchText);
    }

    // Only write when the computed params actually differ from the current
    // URL. This prevents wiping shared-link filters on the first render (when
    // the URL is already correct) and avoids an update loop with the sync
    // effect above.
    if (newSearchParams.toString() !== searchParams.toString()) {
      setSearchParams(newSearchParams, { replace: true });
    }

    // Set filtering flag when any filters are active
    if (statusFilter.length > 0 || driverFilter || debouncedCustomerSearchText) {
      setIsFiltering(true);
    }
  }, [
    statusFilter,
    driverFilter,
    debouncedCustomerSearchText,
    searchParams,
    setSearchParams,
  ]);

  // Set filtering flag when driverFilter changes
  useEffect(() => {
    setIsFiltering(true);
  }, [driverFilter]);

  // Set filtering flag when status filter changes
  useEffect(() => {
    setIsFiltering(true);
  }, [statusFilter]);

  // Set filtering flag when customer search changes
  useEffect(() => {
    if (customerSearchText !== "") {
      setIsFiltering(true);
    }
  }, [customerSearchText]);

  // Clear all filters
  const clearFilters = () => {
    setIsFiltering(true); // Set filtering flag
    setStatusFilter([]);
    setDriverFilter(undefined);
    setCustomerSearchText("");
    setDebouncedCustomerSearchText("");

    // Preserve the order ID when clearing filters
    const orderId = searchParams.get("id");
    if (orderId) {
      const newSearchParams = new URLSearchParams();
      newSearchParams.set("id", orderId);
      setSearchParams(newSearchParams, { replace: true });
    } else {
      // Hvis det ikke er noen ID-parameter, fjern alle parametere
      setSearchParams({}, { replace: true });
    }
  };

  const statusFilterCount = statusFilter.length;
  const driverFilterActive = driverFilter ? 1 : 0;
  const hasActiveFilters =
    statusFilter.length > 0 ||
    driverFilter !== undefined ||
    customerSearchText.length > 0;

  return {
    statusFilter,
    setStatusFilter,
    driverFilter,
    setDriverFilter,
    customerSearchText,
    setCustomerSearchText,
    debouncedCustomerSearchText,
    isFiltering,
    setIsFiltering,
    clearFilters,
    statusFilterCount,
    driverFilterActive,
    hasActiveFilters,
  };
};
